import { AuthSessionStore } from "../api/auth_session.js";
import { adoptEconomyFromPlayer } from "../api/player_sync.js";
import { finishConveyorRun, retryConveyorLevel, startConveyorRun } from "../api/requests/conveyor.requests.js";
import type { ConveyorLevelDto } from "../api/requests/conveyor.requests.js";
import type { DailyTaskRewardDto } from "../api/dto/daily_tasks.dto.js";
import { ConveyorConfigService } from "../conveyor_config/conveyor_config_service.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";

export interface ConveyorFinish
{
  level: number;
  best_level: number;
  seconds: number;
  retry_price: number;
  granted: DailyTaskRewardDto[];
}

// The server owns the level, the rewards and the retry; the local statistics
// only mirror its answers so the UI has something to show offline.
export class ConveyorRun
{
  private static readonly REQUEST_TIMEOUT_MS = 5000;

  public static level(): number
  {
    return StatisticsStorage.load().conveyor_level;
  }

  public static bestLevel(): number
  {
    return StatisticsStorage.load().conveyor_best_level;
  }

  public static async start(): Promise<ConveyorLevelDto | null>
  {
    if ( !AuthSessionStore.isLoggedIn() )
      return null;

    try
    {
      const state = await startConveyorRun();
      StatisticsStorage.update( { conveyor_level: state.level, conveyor_best_level: state.best_level, conveyor_lost_level: null } );
      return state;
    }
    catch ( e )
    {
      console.warn( "[ConveyorRun] start failed", e );
      return null;
    }
  }

  public static async finish( won: boolean ): Promise<ConveyorFinish>
  {
    const level = ConveyorRun.level();
    const spec = ConveyorConfigService.levelAt( level );

    if ( AuthSessionStore.isLoggedIn() )
    {
      try
      {
        const result = await finishConveyorRun( won, ConveyorRun.REQUEST_TIMEOUT_MS );
        if ( result.player )
          adoptEconomyFromPlayer( result.player );
        StatisticsStorage.update( {
          conveyor_level: result.next_level,
          conveyor_best_level: result.best_level,
          conveyor_lost_level: won ? null : result.level,
        } );
        return { level: result.level, best_level: result.best_level, seconds: result.seconds, retry_price: result.retry_price, granted: result.granted };
      }
      catch ( e )
      {
        console.warn( "[ConveyorRun] finish failed", e );
      }
    }

    const best_level = won ? Math.max( ConveyorRun.bestLevel(), level ) : ConveyorRun.bestLevel();
    StatisticsStorage.update( { conveyor_level: won ? level + 1 : 1, conveyor_best_level: best_level, conveyor_lost_level: null } );
    return { level, best_level, seconds: spec.seconds, retry_price: spec.retry_price, granted: [] };
  }

  public static canRetry(): boolean
  {
    return AuthSessionStore.isLoggedIn() && StatisticsStorage.load().conveyor_lost_level !== null;
  }

  public static async retry(): Promise<boolean>
  {
    try
    {
      const result = await retryConveyorLevel( ConveyorRun.REQUEST_TIMEOUT_MS );
      if ( result.player )
        adoptEconomyFromPlayer( result.player );
      StatisticsStorage.update( { conveyor_level: result.level, conveyor_lost_level: null } );
      return true;
    }
    catch ( e )
    {
      console.warn( "[ConveyorRun] retry failed", e );
      return false;
    }
  }
}
